import React from "react";
import ReactDOM from "react-dom";
import { Dialog, RaisedButton, Tabs, Tab } from "material-ui";
import set from "lodash/set";
import get from "lodash/get";
import cloneDeep from "lodash/cloneDeep";
import mapValues from "lodash/mapValues";
import throttle from "lodash/throttle";

import ConfiguratorSection from "./partials/configurator/configurator-section.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import EventBucketActions from "../actions/EventBucketActions";
import EventBucketStore from "../stores/EventBucketStore";
import UserStore from "../stores/UserStore";

class EventBucketConfigurator extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      iframeLoading: true,
      tabIndex: null,
      currentSection: null,
      eventBucket: null,
      currentForcedBucket: null,
      config: null,
      hasUnsavedChanges: false,
      isSaving: false,
      lastUpdated: null,
      fullWidthControls: false,
      forceFullWidthControls: false,
      iframeWidth: null
    };

    this.changeTab = this.changeTab.bind(this);
    this.onTabChange = this.onTabChange.bind(this);

    this._postMessage = this._postMessage.bind(this);

    // Debounced to prevent laggy UI (e.g. while typing text)
    this.updatePreview = throttle(this.updatePreview.bind(this), 250);

    this.onConfigFocus = this.onConfigFocus.bind(this);
    this.onConfigBlur = this.onConfigBlur.bind(this);
    this.onConfigChange = this.onConfigChange.bind(this);
    this.onForcePreview = this.onForcePreview.bind(this);
    this.onEndForcePreview = this.onEndForcePreview.bind(this);

    this.close = this.close.bind(this);
    this.save = this.save.bind(this);
    this.toggleFullWidth = this.toggleFullWidth.bind(this);

    this.onResize = this.onResize.bind(this);

    this.iframe = null;
    this.container = null;
    this.sectionContainer = null;
  }

  static getStores() {
    return [EventBucketStore, UserStore];
  }

  static getPropsFromStores() {
    return {
      getEventBucketById: EventBucketStore.getEventBucketById,
      getConfigSchema: EventBucketStore.getConfigSchema
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  // Actually change the tab displayed in UI
  changeTab(key, props) {
    props = props || this.props;

    let index = props
      .getConfigSchema(props.params.eventBucketId)
      .sections.findIndex(s => s.key === key);

    this.setState(
      {
        tabIndex: index,
        currentSection: props.getConfigSchema(props.params.eventBucketId)
          .sections[index]
      },
      () => {
        // Scroll to top of section
        ReactDOM.findDOMNode(this.sectionContainer).parentNode.scrollTop = 0;

        this.onConfigFocus(
          this.state.currentSection.key,
          this.state.currentSection
        );
      }
    );
  }

  // When the user attempts to change tabs - push the new url
  onTabChange(tabIndex, props) {
    props = props || this.props;

    // https://github.com/callemall/material-ui/issues/2189
    if (typeof tabIndex !== "number") {
      return;
    }

    let key = props.getConfigSchema(props.params.eventBucketId).sections[
      tabIndex
    ].key;
    this.context.router.push(
      `/event_buckets/${props.params.eventBucketId}/settings/configurator/${key}`
    );
  }

  _postMessage(name, data) {
    if (!this.iframe) {
      return;
    }

    let targetWindow = this.iframe.contentWindow;

    targetWindow.postMessage(
      {
        name: name,
        value: data
      },
      process.env.CONFIGURATOR_FRONTEND_URL || this.state.eventBucket.urls.root
    );
  }

  updatePreview() {
    this._postMessage("tenant-update", this.state.config);
  }

  onConfigFocus(key, item = {}, value = {}) {
    if (key === "menus") {
      this._postMessage("open-left-nav");
    } else if (item.preview_location) {
      let location = cloneDeep(item.preview_location);
      if (item.preview_location.dynamic) {
        location.params = mapValues(location.params, v => {
          // Support @value|property_name for dynamic values based on
          // the focused config item's value.
          const VALUE_KEY = "@value|";
          if (v.indexOf(VALUE_KEY) === 0) {
            return value[v.substring(VALUE_KEY.length)];
          }

          return v;
        });
      }

      this._postMessage("go-to-location", location);
    } else if (this.state.currentSection.key !== "menus") {
      this._postMessage("close-left-nav");
    }

    this.setState({
      fullWidthControls: ["misc", "virtual"].includes(
        this.state.currentSection.key
      )
    });
  }

  onConfigBlur(key, item = {}) {
    if (item.preview_blur_location) {
      this._postMessage("go-to-location", item.preview_blur_location);
    }
  }

  onConfigChange(key, value, options = {}) {
    if (options.file) {
      this.onConfigFileUpload(key, value);
      return;
    }

    let config = Object.assign({}, this.state.config);
    set(config, key, value);
    this.setState({ config, hasUnsavedChanges: true }, () => {
      this.updatePreview();
    });
  }

  onConfigFileUpload(key, file) {
    EventBucketActions.uploadFile(this.props.params.eventBucketId, key, file);
  }

  onForcePreview(settings, item) {
    this.setState(
      {
        currentForcedBucket: Object.assign(
          {},
          this.state.currentForcedBucket,
          settings
        )
      },
      () => {
        let tempBucket = Object.assign(
          {},
          this.state.eventBucket,
          this.state.currentForcedBucket
        );
        this._postMessage("force-event-bucket", tempBucket);
        // trigger refresh
        if (item) {
          this.onConfigFocus(item.key, item, get(this.state.config, item.key));
        }
      }
    );
  }

  onEndForcePreview(settings) {
    let newForcedBucket = Object.assign({}, this.state.currentForcedBucket);

    Object.keys(settings).map(k => {
      delete newForcedBucket[k];
    });

    this.setState(
      {
        currentForcedBucket: Object.keys(newForcedBucket).length
          ? newForcedBucket
          : null
      },
      () => {
        this._postMessage(
          "force-event-bucket",
          this.state.currentForcedBucket || this.state.eventBucket
        );
      }
    );
  }

  close() {
    this.context.router.push(
      `/event_buckets/${this.props.params.eventBucketId}/settings`
    );
  }

  save() {
    this.setState({
      isSaving: true,
      lastUpdated: this.state.eventBucket.updated_at
    });

    EventBucketActions.edit(this.props.params.eventBucketId, {
      frontend_config: JSON.stringify(this.state.config)
    });
  }

  toggleFullWidth() {
    this.setState({
      forceFullWidthControls: !this.state.forceFullWidthControls
    });
  }

  componentDidMount() {
    this.context.router.setRouteLeaveHook(this.props.route, nextLocation => {
      if (
        this.state.hasUnsavedChanges &&
        (!nextLocation || nextLocation.pathname.indexOf("configurator") === -1)
      ) {
        return "Are you sure you wish to leave the configurator? Your changes will not be saved.";
      }
    });

    window.addEventListener("resize", this.onResize);

    EventBucketActions.fetchOne.defer(this.props.params.eventBucketId);
    EventBucketActions.fetchConfigSchema.defer();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.onResize);
  }

  onResize() {
    if (this.container) {
      this.setState({
        iframeWidth: this.container.clientWidth * 1.3 - 300
      });
    }
  }

  componentWillReceiveProps(props) {
    let bucket = props.getEventBucketById(props.params.eventBucketId),
      schema = props.getConfigSchema(props.params.eventBucketId);

    // Handle tab change, or force select if none specified
    if (bucket && schema && schema.sections) {
      if (!props.params.tab) {
        this.onTabChange(0, props);
      }
      if (
        props.params.tab &&
        (!this.state.currentSection ||
          props.params.tab !== this.props.params.tab)
      ) {
        this.changeTab(props.params.tab, props);
      }
    }

    if (bucket && schema && schema.sections && !this.state.eventBucket) {
      this.setState(
        {
          eventBucket: bucket,
          config: cloneDeep(bucket.frontend_config)
        },
        () => {
          // Force initial refresh to sync
          this.iframe.onload = () => {
            this.setState({ iframeLoading: false });
            this.changeTab(this.props.params.tab);
            this.updatePreview();
          };

          // Rescale iframe appropriately
          this.onResize();
        }
      );

      // Update any modified files
    } else if (bucket && bucket.UPLOADED_FILES) {
      let config = cloneDeep(this.state.config),
        modified = Object.assign(config, bucket.UPLOADED_FILES);
      this.setState({ config: modified, hasUnsavedChanges: true }, () => {
        this.updatePreview();
      });
    } else if (
      bucket &&
      this.state.lastUpdated &&
      bucket.updated_at !== this.state.lastUpdated
    ) {
      // Edit completed successfully
      this.setState({ hasUnsavedChanges: false }, () => {
        this.close();
      });
    }
  }

  getStyles() {
    return {
      dialog: {
        width: "95%",
        maxWidth: "none",
        overflow: "hidden"
      },
      dialogBody: {
        padding: 0
      },
      loading: {
        textAlign: "center",
        paddingTop: "30vh",
        paddingBottom: "30vh"
      },
      bodyClear: {
        width: "100%",
        height: "90vh",
        backgroundColor: "#fff"
      },
      controls: {
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        overflowY: "scroll",
        overflowX: "hidden"
      },
      tabItemContainer: {
        position: "fixed",
        top: 0,
        zIndex: 10
      },
      inkBar: {
        position: "fixed",
        top: 50,
        zIndex: 1,
        borderBottom: "1px solid #ccc"
      },
      contentContainer: {
        width: 300,
        backgroundColor: "#fff",
        marginTop: 48
      },
      fullWidthContentContainer: {
        width: "100%"
      },
      controlView: {
        padding: 10,
        overflow: "scroll"
      },
      iframe: {
        position: "absolute",
        top: 50,
        right: 0,
        height: "133%",
        width: "100%",
        transform: "scale(0.7)",
        transformOrigin: "top right",
        border: 0,
        backgroundColor: "#fff",
        borderLeft: "1px solid #ccc"
      },
      iframeLoading: {
        textAlign: "center",
        lineHeight: "100vh",
        fontSize: 30
      },
      fullWidthControlsIframe: {
        display: "none"
      },
      actions: {
        position: "absolute",
        bottom: 0,
        right: 0,
        padding: 20,
        backgroundColor: "rgba(0,0,0,0.5)",
        borderTopLeftRadius: 10
      },
      fullWidthToggle: {
        textAlign: "center",
        fontSize: 10,
        position: "fixed",
        bottom: 7,
        left: 10,
        backgroundColor: "white",
        zIndex: 99999,
        padding: "2px 5px",
        borderRadius: 4,
        border: "1px solid #ccc",
        MozUserSelect: "none",
        WebkitUserSelect: "none",
        msUserSelect: "none",
        cursor: "pointer",
        textTransform: "uppercase"
      }
    };
  }

  render() {
    var styles = this.getStyles(),
      {
        iframeLoading,
        tabIndex,
        isSaving,
        eventBucket,
        config,
        fullWidthControls,
        forceFullWidthControls
      } = this.state,
      configSchema = this.props.getConfigSchema(
        this.props.params.eventBucketId
      );

    var isFullWidth = fullWidthControls || forceFullWidthControls;

    return (
      <Dialog
        modal={true}
        contentStyle={styles.dialog}
        bodyStyle={styles.dialogBody}
        open={true}
      >
        {isSaving ? (
          <div style={styles.loading}>Saving...</div>
        ) : config && configSchema ? (
          <div
            className="EventBucketConfigurator--container"
            ref={r => {
              this.container = r;
            }}
          >
            <div style={styles.bodyClear} />
            <div
              style={Object.assign(
                styles.controls,
                isFullWidth && styles.fullWidthControls
              )}
            >
              <Tabs
                onChange={index => this.onTabChange(index)}
                value={tabIndex}
                tabItemContainerStyle={Object.assign(
                  styles.tabItemContainer,
                  isFullWidth && styles.fullWidthTabItemContainer
                )}
                inkBarStyle={styles.inkBar}
                contentContainerStyle={Object.assign(
                  styles.contentContainer,
                  isFullWidth && styles.fullWidthContentContainer
                )}
                ref={r => {
                  this.sectionContainer = r;
                }}
              >
                {configSchema.sections.map((section, i) => {
                  return (
                    <Tab label={section.name} value={i} key={i}>
                      <ConfiguratorSection
                        eventBucket={eventBucket}
                        schema={section}
                        config={config}
                        onConfigChange={this.onConfigChange}
                        onConfigFocus={this.onConfigFocus}
                        onConfigBlur={this.onConfigBlur}
                        onForcePreview={this.onForcePreview}
                        onEndForcePreview={this.onEndForcePreview}
                        isFullWidth={isFullWidth}
                        isGod={UserStore.isGod()}
                        isEnterprise={UserStore.isEnterprise()}
                      />
                      <div
                        style={styles.fullWidthToggle}
                        onTouchTap={this.toggleFullWidth}
                      >
                        Full width controls
                      </div>
                    </Tab>
                  );
                })}
              </Tabs>
            </div>
            <iframe
              ref={r => {
                this.iframe = r;
              }}
              src={
                process.env.CONFIGURATOR_FRONTEND_URL || eventBucket.urls.root
              }
              style={Object.assign(
                styles.iframe,
                isFullWidth && styles.fullWidthControlsIframe,
                { width: this.state.iframeWidth }
              )}
            />
            {iframeLoading ? (
              <div style={styles.iframe}>
                <div style={styles.iframeLoading}>Loading live preview...</div>
              </div>
            ) : null}
            <div style={styles.actions}>
              <RaisedButton label="Discard changes" onTouchTap={this.close} />
              <RaisedButton
                label="Save"
                primary={true}
                onTouchTap={this.save}
                style={{ marginLeft: 7 }}
              />
            </div>
          </div>
        ) : (
          <div style={styles.loading}>Loading...</div>
        )}
      </Dialog>
    );
  }
}

export default connectToStores(EventBucketConfigurator);



// WEBPACK FOOTER //
// ./src/app/components/event-bucket-configurator.jsx